import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { Drawer, DrawerContent, Portal } from '@chakra-ui/react';
import { CommmentTwoIcon, GoogleIcon } from '@sketchnote/icons';
import { Avatar, AvatarGroup } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { addDays, addHours, differenceInHours, formatISO, isSameDay } from 'date-fns';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import parse from 'date-fns/parseISO';
import startOfWeek from 'date-fns/startOfWeek';
import { useRouter } from 'next/router';
import * as React from 'react';
import { dateFnsLocalizer, Event } from 'react-big-calendar';
import { Calendar } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { Node } from 'slate';
import { useBoardStore } from 'src/features/boards';
import { useKanbanStore } from 'src/features/kanban';
import { CreateTask } from 'src/features/kanban/component/Item';
import { KanbanState } from 'src/features/kanban/store/types';
import { TaskEdit } from 'src/features/tasks';
import { shallow } from 'zustand/shallow';

import { Customization } from './Customization';
import { Toolbar } from './Toolbar';
import { getGoogleEvents } from './utils';

const { useMemo, useState, useEffect } = React;

const DnDCalendar = withDragAndDrop(({ ...props }) => <Calendar {...props} />);

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export const onDropDateTimeRange = (
  isEventAllDay: boolean,
  isSlotAllDay: boolean,
  start: Date,
  end: Date
) => {
  if (isEventAllDay && !isSlotAllDay) {
    if (format(start, 'hh:mm aa') !== '12:00 AM') {
      return [formatISO(start), formatISO(addHours(start, 1))];
    }
  }
  if (!isEventAllDay && isSlotAllDay) {
    if (isSameDay(start, end)) {
      return [
        formatISO(new Date(start.toDateString())),
        formatISO(new Date(addDays(start, 1).toDateString())),
      ];
    }
    return [formatISO(new Date(start.toDateString())), formatISO(new Date(end.toDateString()))];
  }
  return [formatISO(start), formatISO(end)];
};

export const CalendarView = () => {
  const boardData = useBoardStore();
  const {
    columns,
    setItem,
    currentSelectedItem,
    onUpdateItemField,
    onDeleteItem,
    onDuplicateItem,
    isReadOnly,
    onChangeCurrentSelectedItem,
    onCloseCurrentSelectedItem,
  } = useKanbanStore(boardSelector, shallow);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const [isDuplicatePromptOpen, setIsDuplicatePromptOpen] = useState<boolean>(false);
  const [googleEvents, setGoogleEvents] = useState<any[]>([]);
  const calendarCustomize = useBoardStore((s) => s.calendarCustomize);
  const toggleCalendarCustomize = useBoardStore((s) => s.toggleCalendarCustomize);
  const router = useRouter();
  const orgId = router?.query?.orgId;
  const [currentTaskId, setCurrentTaskId] = useState();
  const [currentColumnId, setCurrentColumnId] = useState();
  const mode = router?.query?.mode;
  // const { googleEvents } = useGoogleEvents(new Date());
  // TODO: fetch for start of the month till end of the month
  const isEventAllDay = (dates) => {
    if (dates.length > 1) {
      if (differenceInHours(new Date(dates[1]), new Date(dates[0])) < 24) return false;
    }
    return true;
  };
  const events = useMemo(() => {
    const flattenedTasksFromCols = columns.map((column) => column.items).flat();

    const subTasks = flattenedTasksFromCols
      .filter((t) => (t.subtasks || []).length)
      .map((i) => i.subtasks)
      .flat();

    const subTaskEvents = (subTasks as any[]).map((item) => {
      const parent = flattenedTasksFromCols.find((t) => t.id === item.parent);
      const result = {
        allDay: isEventAllDay(item.assignDate),
        title: getFormattedValue(item.name) || 'Untitled',
        resource: {
          type: 'subtask',
          itemId: item._id,
          columnId: item.column,
          parentName: (parent?.name && getFormattedValue(parent.name)) || 'Untitled',
        },
      } as Event;

      const start = item.assignDate?.[0] && parse(item.assignDate?.[0]);
      const end = item.assignDate?.[1] && parse(item.assignDate?.[1]);
      if (start) {
        result.start = start;
      }
      if (end) {
        result.end = end;
      } else if (start) {
        result.end = start;
      }
      return result;
    });

    const taskEvents = flattenedTasksFromCols.map((item) => {
      const result = {
        allDay: isEventAllDay(item.assignDate),
        title: getFormattedValue(item.name) || 'Untitled',
        resource: {
          type: 'task',
          itemId: item.id,
          columnId: item.column,
          columnColor: columns?.filter((column) => column.id === item.column)[0]?.color || '#FFFF',
          completed: item?.completed,
          labels: item?.label,
          assignee: item?.assignee,
        },
      } as Event;

      if (calendarCustomize.showAssignee) {
        result.resource.assignee = item.assignee;
      }
      if (calendarCustomize.showCommentsIcon) {
        result.resource.commentCount = item.commentsCount || 0;
      }

      const start = item.assignDate?.[0] && parse(item.assignDate?.[0]);
      const end = item.assignDate?.[1] && parse(item.assignDate?.[1]);
      if (start) {
        result.start = start;
      }
      if (end) {
        result.end = end;
      } else if (start) {
        result.end = start;
      }
      return result;
    });
    return taskEvents.concat(subTaskEvents).concat(googleEvents);
  }, [columns, calendarCustomize, googleEvents]);

  const handleTaskViewClose = () => {
    setCurrentTaskId(undefined);
    setCurrentColumnId(undefined);
    router.push({
      pathname: boardData.isPublicView
        ? `/${mode}/${orgId}/files/${router.query.slug}/public`
        : `/${mode}/${orgId}/files/${router.query.slug}`,
      query: {
        view: router.query.view,
        viewId: router.query.viewId,
        type: router.query.type,
      },
    });
  };

  const onToggleDeletePrompt =
    (isOpen = true) =>
    () => {
      setIsDeletePromptOpen(isOpen);
    };
  const onToggleDuplicatePrompt =
    (isOpen = true) =>
    () => {
      setIsDuplicatePromptOpen(isOpen);
    };

  useEffect(() => {
    if (!router?.query?.taskId) {
      onCloseCurrentSelectedItem();
    }
  }, [onCloseCurrentSelectedItem, router]);

  const handleDeleteTask = async () => {
    if (currentTaskId && currentColumnId) {
      onDeleteItem({
        columnId: currentColumnId,
        itemId: currentTaskId,
      });
      setIsDeletePromptOpen(false);
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
      );
    }
  };

  const handleDuplicateTask = () => {
    if (currentSelectedItem.item?.id && currentSelectedItem.columnId) {
      onDuplicateItem({
        columnId: currentSelectedItem.columnId,
        itemId: currentSelectedItem.item.id,
      });
      setIsDuplicatePromptOpen(false);
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
      );
    }
  };

  const onNavigate = async (newDate: Date) => {
    const events = await getGoogleEvents(newDate);
    setGoogleEvents(events);
  };

  useEffect(() => {
    const loadGoogleCalEvents = async () => {
      const events = await getGoogleEvents(new Date());
      setGoogleEvents(events);
    };
    loadGoogleCalEvents();
  }, []);

  const agendaRangeFormat = ({ start, end }, culture, local) => {
    const dateFormat = 'EEE MMM d';
    if (isSameDay(start, end)) {
      return local.format(start, dateFormat, culture);
    }
    return (
      local.format(start, dateFormat, culture) + ' — ' + local.format(end, dateFormat, culture)
    );
  };

  return (
    <div className="bg-white h-full">
      <div className="px-100 py-30 relative h-900 bg-white">
        <DnDCalendar
          localizer={localizer}
          className="calendar-view"
          components={{
            toolbar: Toolbar,
            event: (e) => (
              <Tippy
                content={e.event.resource.parentName}
                disabled={e.event.resource.type !== 'subtask'}
              >
                <div
                  title={
                    !e.isAllDay && e.event.start && e.event.end
                      ? `${format(e.event.start, 'hh:mm aa')} - ${format(e.event.end, 'hh:mm aa')}`
                      : ''
                  }
                  style={{
                    backgroundColor: e?.event?.resource?.columnColor,
                  }}
                  className={`py-4 px-5 text-sm font-regular rounded-md truncate text-gray-600 ${
                    e.event.resource.completed ? 'opacity-50' : 'opacity-100'
                  }`}
                >
                  <div className="flex justify-between">
                    <div className="flex items-center ">
                      {e.event.resource.type === 'google' && (
                        <GoogleIcon className="mr-4" size={10} />
                      )}
                      {e.event.resource.type === 'subtask' && (
                        <span className="text-xss mr-4">⮑</span>
                      )}
                      <p>{e.title || <span className="text-gray-300">Untitled</span>}</p>
                    </div>

                    <div
                      className={cn({
                        'mt-8 flex items-center':
                          e.event.resource?.assignee?.length > 0 ||
                          (e.event.resource?.commentCount && e.event.resource?.commentCount > 0),
                      })}
                    >
                      {/* {e.event.resource?.assignee?.length > 0 && (
                      <div className="flex">
                        <AvatarGroup maxCount={1}>
                          {e.event.resource.assignee.map((obj) => (
                            <Avatar
                              name={cn(obj.firstname, obj.lastname)}
                              src={obj.avatar}
                              type="photo"
                              key={obj.memberId}
                              wrapperClassName="-mr-4 border-2 border-white"
                            />
                          ))}
                        </AvatarGroup>
                        {e.event.resource.assignee.length > 2 && (
                          <Avatar
                            name={e.event.resource.assignee.length - 2}
                            tooltip={false}
                            type={'initials'}
                            src={''}
                            wrapperClassName="-ml-10 border-white border-1 transform transition duration-500 hover:scale-125"
                          />
                        )}
                      </div>
                    )} */}
                      {e.event.resource?.commentCount && e.event.resource?.commentCount > 0 ? (
                        <div className="flex text-gray-500 items-center text-xs ml-10">
                          <CommmentTwoIcon />

                          <span className="ml-4">{e.event.resource.commentsCount}</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="mx-8 flex items-center">
                      {e.event.resource?.labels?.length > 0
                        ? e.event.resource?.labels?.map((label, index) => {
                            return (
                              <Tippy
                                key={index}
                                placement="bottom"
                                content={`Label: ${label.label}`}
                              >
                                <div
                                  className="h-10 w-10 rounded-full mx-1"
                                  style={{ backgroundColor: label.color }}
                                ></div>
                              </Tippy>
                            );
                          })
                        : null}
                    </div>
                  </div>
                </div>
              </Tippy>
            ),
          }}
          onSelectEvent={(e) => {
            if (e.resource.type !== 'google') {
              setCurrentTaskId(e.resource.itemId);
              setCurrentColumnId(e.resource.columnId);
              return router.push({
                pathname: boardData.isPublicView
                  ? `/${mode}/${orgId}/files/${router.query.slug}/public`
                  : `/${mode}/${orgId}/files/${router.query.slug}`,
                query: {
                  view: router.query.view,
                  viewId: router.query.viewId,
                  type: router.query.type,
                  taskId: e.resource.itemId,
                },
              });
            }
            if (e.resource.type === 'google') {
              const { googleCalendarId, googleEventId } = e.resource;
              if (!googleCalendarId || !googleEventId) return;
              const eid = btoa(`${googleEventId} ${googleCalendarId}`);
              const url = `https://www.google.com/calendar/event?eid=${eid}&authuser=${e.resource.calendarEmail}`;
              return window.open(url, '_blank');
            }
          }}
          onDoubleClickEvent={(e) => {
            if (e.resource.type !== 'google') {
              setCurrentTaskId(e.resource.itemId);
              router.push({
                pathname: boardData.isPublicView
                  ? `/${mode}/${orgId}/files/${router.query.slug}/public`
                  : `/${mode}/${orgId}/files/${router.query.slug}`,
                query: {
                  view: router.query.view,
                  viewId: router.query.viewId,
                  type: router.query.type,
                  taskId: e.resource.itemId,
                },
              });
            }
          }}
          onSelectSlot={(e) => {
            boardData.isPublicView
              ? null
              : onChangeCurrentSelectedItem({
                  columnId: columns[0].id,
                  mode: 'add',
                  isOpen: true,
                  selectedDate: { end: e.end, start: e.start },
                });
          }}
          selectable={!isReadOnly && boardData.access !== 'view'}
          events={events}
          views={['month', 'week', 'work_week', 'day', 'agenda']}
          startAccessor="start"
          endAccessor="end"
          onEventDrop={({ end, event, start, isAllDay }) => {
            if (!['google'].includes(event.resource.type)) {
              onUpdateItemField({
                item: {
                  assignDate: onDropDateTimeRange(
                    event.allDay || false,
                    isAllDay,
                    start as Date,
                    end as Date
                  ),
                  id: event.resource.itemId,
                },
                keys: ['assignDate'],
                columnId: event.resource.columnId,
              });
            }
          }}
          onEventResize={({ end, event, start }) => {
            if (event.resource.type !== 'google') {
              onUpdateItemField({
                item: {
                  assignDate: [formatISO(start as Date), formatISO(end as Date)],
                  id: event.resource.itemId,
                },
                keys: ['assignDate'],
                columnId: event.resource.columnId,
              });
            }
          }}
          formats={{
            timeGutterFormat: 'h aaa',
            agendaDateFormat: 'EEE, d MMM yyyy',
            agendaHeaderFormat: agendaRangeFormat,
          }}
          onNavigate={onNavigate}
          length={1}
        />

        <Drawer
          size="lg"
          isOpen={calendarCustomize.isOpen}
          placement="right"
          onClose={() => toggleCalendarCustomize(false)}
        >
          <Portal>
            <DrawerContent className="overflow-auto">
              <Customization />
            </DrawerContent>
          </Portal>
        </Drawer>

        {currentSelectedItem.mode === 'add' ? (
          <Modal
            isOpen={currentSelectedItem.mode === 'add' || false}
            onClose={handleTaskViewClose}
            motionPreset="none"
          >
            <ModalOverlay />
            <ModalContent
              style={{
                position: 'relative',
                marginTop: '40vh',
                marginLeft: '30vh',
              }}
              className="
            bg-white rounded-lg static"
            >
              <CreateTask
                startDate={currentSelectedItem.selectedDate?.start}
                endDate={currentSelectedItem.selectedDate?.end}
              />
            </ModalContent>
          </Modal>
        ) : (
          <></>
        )}

        <Modal isOpen={!!router.query.taskId} onClose={handleTaskViewClose} motionPreset="none">
          <ModalOverlay />
          <ModalContent
            style={{
              position: 'initial',
              maxWidth: '120rem',
            }}
            className={`min-w-768 h-vh90 bg-white rounded-lg static`}
          >
            {router.query.taskId && (
              <TaskEdit
                id={router?.query?.taskId as string}
                onDelete={onToggleDeletePrompt(true)}
                onDuplicate={onToggleDuplicatePrompt(true)}
                onClose={handleTaskViewClose}
                onChangeTask={(e) => {
                  if (!e.task.isSubtask && e.task.column && e.task.id) {
                    setItem({
                      columnId: e.task.column,
                      item: e.task as any,
                      itemId: e.task.id,
                    });
                  }
                }}
              />
            )}
          </ModalContent>
        </Modal>

        <Modal
          isOpen={isDeletePromptOpen}
          onClose={() => {
            setIsDeletePromptOpen(false);
          }}
        >
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsDeletePromptOpen(false);
                }}
                className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
              >
                Cancel
              </button>
              <button
                className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                onClick={handleDeleteTask}
              >
                Delete
              </button>
            </div>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isDuplicatePromptOpen}
          onClose={() => {
            setIsDuplicatePromptOpen(false);
          }}
        >
          <ModalOverlay />

          <ModalContent
            style={{
              maxWidth: '46rem',
              padding: '32px',
              marginTop: '20rem',
              borderRadius: '8px',
            }}
          >
            <ModalCloseButton />
            <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setIsDuplicatePromptOpen(false);
                }}
                className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
              >
                Cancel
              </button>
              <button
                className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
                onClick={handleDuplicateTask}
              >
                Duplicate
              </button>
            </div>
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
};
const boardSelector = (s: KanbanState) => ({
  columns: s.columns,
  onAddNewColumn: s.onAddNewColumn,
  currentSelectedItem: s.currentSelectedItem,
  onCloseCurrentSelectedItem: s.onCloseCurrentSelectedItem,
  isReadOnly: s.isReadOnly,
  onUpdateItemPlacement: s.onUpdateItemPlacement,
  onChangeColumnOrder: s.onChangeColumnOrder,
  setItem: s.setItem,
  onDeleteItem: s.onDeleteItem,
  onDuplicateItem: s.onDuplicateItem,
  onChangeCurrentSelectedItem: s.onChangeCurrentSelectedItem,
  onUpdateItemField: s.onUpdateItemField,
});

export function getFormattedValue(value): string {
  if (typeof value === 'string') {
    return value;
  } else if (Array.isArray(value)) {
    return value.map((obj) => Node.string(obj)).join('\n');
  }
  return '';
}
